import { Button, Input, cn } from '@sitewise/ui';
import { Pencil } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

/**
 * Lightweight inline-edit primitive used on the unit Overview tab.
 * Click-to-edit, Enter/blur to save, Escape to cancel. Save is async
 * so the parent can wire it to a mutation; the component handles the
 * "saving" state and surfaces a small inline error if the promise
 * rejects.
 *
 * Specifically scoped to small text/number values; multi-step forms
 * (the Edit unit slide-over) handle their own state.
 */
interface InlineEditProps {
  /** Current rendered value when not editing. `null` shows a faint
   *  placeholder; passing a non-empty `displayValue` overrides the
   *  formatted view (used for date / select pretty-printing). */
  value: string | number | null;
  displayValue?: ReactNode;
  placeholder?: string;
  /** Called with the trimmed string the user typed. The caller is
   *  responsible for parsing into the right runtime type and for
   *  validation that depends on backend rules. */
  onSave: (next: string) => Promise<void> | void;
  inputType?: 'text' | 'number' | 'date';
  className?: string;
  /** Disabled affordance — used when the viewer doesn't have the
   *  right role to edit unit fields. */
  readOnly?: boolean;
  /** Aria label for the toggle / input so screen readers know what
   *  field is being edited. */
  label: string;
}

export function InlineEdit({
  value,
  displayValue,
  placeholder = 'Add',
  onSave,
  inputType = 'text',
  className,
  readOnly,
  label,
}: InlineEditProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (value == null ? '' : String(value)));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value == null ? '' : String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (readOnly) {
    return (
      <span className={cn('text-sm text-fg-secondary', className)}>
        {value == null || value === '' ? (
          <span className="text-fg-muted">—</span>
        ) : (
          (displayValue ?? String(value))
        )}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={`Edit ${label}`}
        className={cn(
          'group inline-flex max-w-full items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-left text-sm transition-colors hover:bg-surface-muted',
          className,
        )}
        onClick={() => setEditing(true)}
      >
        <span className={value == null || value === '' ? 'text-fg-muted' : 'text-fg-secondary'}>
          {value == null || value === '' ? placeholder : (displayValue ?? String(value))}
        </span>
        <Pencil
          className="h-3 w-3 shrink-0 text-fg-muted opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      </button>
    );
  }

  async function commit(nextRaw: string) {
    const next = nextRaw.trim();
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit(draft);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
      setDraft(value == null ? '' : String(value));
      setError(null);
    }
  }

  return (
    <span className={cn('inline-flex flex-col gap-0.5', className)}>
      <Input
        id={inputId}
        ref={inputRef}
        aria-label={label}
        type={inputType}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => void commit(draft)}
        className="h-7 px-1.5 py-0.5 text-sm"
        disabled={saving}
      />
      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </span>
  );
}

/**
 * Yes / No / Unset toggle. Clicking cycles through three states. Used
 * for the boolean utility/appliance fields on the Overview tab so a
 * user can mark "no dishwasher" without diving into the slide-over.
 */
interface InlineYesNoProps {
  value: boolean | null;
  onChange: (next: boolean | null) => Promise<void> | void;
  label: string;
  readOnly?: boolean;
}

export function InlineYesNo({ value, onChange, label, readOnly }: InlineYesNoProps) {
  const [busy, setBusy] = useState(false);

  function nextState(): boolean | null {
    if (value === null) return true;
    if (value === true) return false;
    return null;
  }

  async function cycle() {
    if (readOnly || busy) return;
    setBusy(true);
    try {
      await onChange(nextState());
    } finally {
      setBusy(false);
    }
  }

  const tone =
    value === true
      ? 'bg-success-subtle text-success'
      : value === false
        ? 'bg-danger-subtle text-danger'
        : 'bg-surface-muted text-fg-muted';
  const text = value === true ? 'Yes' : value === false ? 'No' : '—';

  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      aria-label={`Toggle ${label}`}
      onClick={cycle}
      disabled={readOnly || busy}
      className={cn('h-6 px-2 text-xs font-medium tabular-nums', tone)}
    >
      {text}
    </Button>
  );
}
